import type { IMesh } from '@seddi/mesh';
import { type EnvironmentComponent, type SceneEntity, getResourceId } from '@seddi/scene';
import type { Object3D } from '@seddi/three';

import { ArrayDiff } from '../core/reconciler/arrayDiff';
import { PropsDiff } from '../core/reconciler/propsDiff';
import type { EntityRenderable } from '../renderables/entityRenderable';
import { attachGarmentGeometry as attachGarmentGeometryToGarment } from '../renderables/garmentRenderable';
import { attachMesh, attachMaterial as attachMeshMaterial } from '../renderables/meshRenderable';
import {
  attachModel,
  recreateGroups as recreateModelGroups,
  recreateMaterials as recreateModelMaterials,
} from '../renderables/modelRenderable';
import { attachButton } from '../renderables/pieceButtonRenderable';
import { attachLogo } from '../renderables/pieceLogoRenderable';
import {
  attachGarmentGeometry as attachGarmentGeometryToPiece,
  attachFabric as attachFabricToPiece,
  attachStitchesTextures,
  recreateGroups as recreatePieceGroups,
  recreateMaterials as recreatePieceMaterials,
  updateLogosVisibility,
} from '../renderables/pieceRenderable';
import {
  type SceneRenderable,
  attachEnvironment,
  createEntity,
  detachEnvironment,
  disposeEntity,
} from '../renderables/sceneRenderable';
import { attachGarmentGeometry as attachGarmentGeometryToSeam } from '../renderables/seamRenderable';
import { attachFabric as attachFabricToSeamTape } from '../renderables/seamTapeRenderable';
import {
  filterEntitiesWithParent,
  filterWithMesh,
  filterWithModel,
  filterWithPiece,
  filterWithSeam,
  filterWithSeamTape,
  findRoot,
  findWithEnvironment,
  findWithGarment,
} from '../scene/scene';
import { ResourcesContext } from '../resources/resourcesContext';
import { type ResourceEvent } from '../resources/resourceEvent';
import { SceneDesc } from '../scene/sceneTypes';
import { themeDefault } from '../theme/themeDefault';
import { Theme } from '../theme/themeTypes';
import { update1st as updateEntity1st, update2nd as updateEntity2nd } from './entityReconciler';
import { isSameFabric } from './fabricHolder';
import { type SceneReconcilerConfig, create as createConfig } from './sceneReconcilerConfig';
import {
  type SceneReconcilerFrame,
  create as createFrame,
  reset as resetFrame,
} from './sceneReconcilerFrame';
import { VisibilityState } from './visibilityState';
import { isGarmentGeometryRequired } from './garmentReconciler';

const GROUND_MATERIAL = '5ee23f93086d40160cbee43c';

/**
 * Scene reconciler.
 */
export class SceneReconciler {
  readonly config: SceneReconcilerConfig;
  readonly resources: ResourcesContext;
  readonly scene: SceneRenderable;
  readonly theme: Theme;

  needsUpdate: boolean;
  readonly renderables: Map<string, Object3D>;
  state?: SceneDesc;

  /**
   * Dispose.
   */
  dispose: () => void;

  private readonly frame: SceneReconcilerFrame;

  /**
   * @param resources - Resources context.
   * @param scene - Scene.
   * @param theme - Theme.
   */
  constructor(
    resources: ResourcesContext,
    scene: SceneRenderable,
    config?: Partial<SceneReconcilerConfig>,
    theme?: Theme,
  ) {
    this.config = createConfig(config);
    this.resources = resources;
    this.scene = scene;
    this.theme = theme ?? themeDefault;

    this.needsUpdate = false;
    this.renderables = new Map();

    this.frame = createFrame(this.config, resources, scene, this.theme);

    //
    // Internal
    //

    const scope = this;

    // handle resource ready event
    const handleResourceReady = (event: Event) => {
      const doButton = (id: string) => {
        const { resources, scene } = scope;

        for (const entity of filterWithPiece(scope.state)) {
          const piece = entity.components.garmentPiece;
          const pieceButtons = piece.buttons.filter((b) => b.resourceID === id);
          const pieceRenderable = scene.entities.get(entity._id)!.piece!;

          for (const pieceButton of pieceButtons) {
            const _id = pieceButton._id;
            const buttonRenderable = pieceRenderable.buttons.find((b) => b.id === _id);
            attachButton(buttonRenderable, pieceButton, resources);
          }

          scope.needsUpdate = true;
        }
      };

      const doEnvironment = (id: string) => {
        const entity = findWithEnvironment(scope.state);
        if (!entity || entity.components.environment.environment.id !== id) {
          return;
        }

        attachEnvironment(scope.scene, id, scope.resources);
        scope.needsUpdate = true;
      };

      const doFabric = (id: string) => {
        const { resources, scene } = scope;

        // Fabric ID without recolor variant.
        const fabricTokens = id.split(':');
        const fabricId = fabricTokens[0];
        const fabricColor = fabricTokens.length > 1 ? fabricTokens[1] : undefined;

        // Pieces with fabric.
        const entities = filterWithPiece(scope.state).filter((e) =>
          isSameFabric(e.components.garmentPiece, fabricId, fabricColor),
        );

        for (const entity of entities) {
          const renderable = scene.entities.get(entity._id)!.piece!;
          attachFabricToPiece(renderable, id, entity.components.garmentPiece!.grain, resources);
          scope.needsUpdate = true;
        }

        // Seam tapes with fabric.
        const seams = filterWithSeamTape(scope.state).filter((e) =>
          isSameFabric(e.components.garmentSeam.tape, fabricId, fabricColor),
        );

        for (const entity of seams) {
          const renderable = scene.entities.get(entity._id)!.seam!.tape!;
          attachFabricToSeamTape(renderable, id, entity.components.garmentSeam!.tape.grain, resources);
          scope.needsUpdate = true;
        }
      };

      const doGarmentGeometry = (id: string) => {
        // Attach to garment.
        const garment = findWithGarment(scope.state);
        const garmentRen = scene.entities.get(garment._id)!.garment!;
        attachGarmentGeometryToGarment(garmentRen, id, resources);

        const garmentGeometry = resources.findGarmentGeometry(id)!;

        // Attach to pieces.
        const pieces = filterWithPiece(scope.state);
        for (const entity of pieces) {
          const piece = entity.components.garmentPiece!;
          const pieceRenderable = scene.entities.get(entity._id)!.piece!;
          attachGarmentGeometryToPiece(pieceRenderable, piece, garmentGeometry, resources);
        }

        // Attach to seams.
        const seams = filterWithSeam(scope.state);
        for (const entity of seams) {
          const seam = entity.components.garmentSeam!;
          const seamRenderable = scene.entities.get(entity._id)!.seam!!;
          attachGarmentGeometryToSeam(seamRenderable, seam, garmentGeometry, resources);
        }

        scope.needsUpdate = true;
      };

      const doLogo = (id: string) => {
        const { resources, scene } = scope;

        const entities = scope.state.children.filter(
          (e) => !!e.components.garmentPiece?.logos || !!e.components.model?.logos,
        );

        for (const entity of entities) {
          const { garmentPiece, model } = entity.components;
          const logos = (garmentPiece?.logos || model!.logos!).filter((l) => l.resource === id);
          if (!logos.length) {
            continue;
          }

          const entityRen = scene.entities.get(entity._id)!;
          const componentRen = entityRen.piece || entityRen.model!;

          const logosDict = componentRen.logos.reduce((dict, logo) => {
            dict.set(logo.id, logo);
            return dict;
          }, new Map());

          for (const logo of logos) {
            const logoRen = logosDict.get(logo._id);
            attachLogo(logoRen, logo, resources);
          }

          if (garmentPiece) {
            recreatePieceMaterials(entityRen.piece!);
            recreatePieceGroups(entityRen.piece!);
          } else if (model) {
            recreateModelMaterials(entityRen.model!);
            recreateModelGroups(entityRen.model!);
          }
          scope.needsUpdate = true;
        }
      };

      const doMaterial = (id: string) => {
        const { resources, scene } = scope;

        for (const entity of filterWithMesh(scope.state)) {
          const materialRef = entity.components.meshRenderable!.material;
          const materialId = getResourceId(materialRef);
          if (materialId === id) {
            const renderable = scene.entities.get(entity._id)!.mesh!;
            attachMeshMaterial(renderable, id, resources);
            scope.needsUpdate = true;
          }
        }
      };

      const doMesh = (id: string) => {
        const { resources, scene } = scope;

        for (const entity of filterWithMesh(scope.state)) {
          const mesh = entity.components.mesh.mesh as IMesh;

          if (mesh.id === id) {
            const renderable = scene.entities.get(entity._id)!.mesh!;
            attachMesh(renderable, id, resources);
            scope.needsUpdate = true;
          }

          if (id === 'avatarHiRes' && entity.components.avatar) {
            const renderable = scene.entities.get(entity._id)!.mesh!;
            attachMesh(renderable, id, resources);
            scope.needsUpdate = true;
          }
        }
      };

      const doModel = (id: string) => {
        const { resources, scene } = scope;

        // Filter entities with model
        const entities = filterWithModel(scope.state).filter(
          (e) => getResourceId(e.components.model.model) === id,
        );

        for (const entity of entities) {
          const renderable = scene.entities.get(entity._id)!.model!;
          attachModel(renderable, id, resources);
          scope.needsUpdate = true;
        }
      };

      const doStitchesTextures = (id: string) => {
        const stitchesTextures = resources.findStitchesTextures(id);

        for (const entity of filterWithPiece(scope.state)) {
          const renderable = scene.entities.get(entity._id)!.piece!;
          const uvs = entity.components.garmentPiece!.geometry.position2D;
          attachStitchesTextures(renderable, uvs, stitchesTextures);
        }
        // TODO: seams tapes
      };

      // early out no scene state
      if (!scope.state) {
        return;
      }

      const resourceEvent = event as ResourceEvent;

      // early out only interested in ready changes
      if (resourceEvent.status !== 'ready') {
        return;
      }

      const { id, kind } = resourceEvent;

      switch (kind) {
        case 'Button': {
          doButton(id);
          break;
        }
        case 'Environment': {
          doEnvironment(id);
          break;
        }
        case 'Fabric': {
          doFabric(id);
          break;
        }
        case 'GarmentGeometry': {
          doGarmentGeometry(id);
          break;
        }
        case 'Logo': {
          doLogo(id);
          break;
        }
        case 'Material': {
          doMaterial(id);
          break;
        }
        case 'Mesh': {
          doMesh(id);
          break;
        }
        case 'Model': {
          doModel(id);
          break;
        }
        case 'StitchesTextures': {
          doStitchesTextures(id);
        }
      }
    };

    this.dispose = function () {
      scope.resources.removeEventListener('resourcechange', handleResourceReady);
    };

    this.resources.addEventListener('resourcechange', handleResourceReady);
  }

  /**
   * Get entity.
   * @param id - Entity ID.
   */
  getEntity(id: string): EntityRenderable | undefined {
    return this.scene.entities.get(id);
  }

  /**
   * Update scene.
   * @param nextScene - Next scene state.
   * @param useGarmentGeometry - Use garment geometry.
   */
  update(nextScene: SceneDesc, useGarmentGeometry?: boolean): boolean {
    const { frame, renderables, resources, scene, state: prevScene } = this;

    // Reset frame.
    resetFrame(frame);
    frame.nextScene = nextScene;
    frame.prevScene = prevScene;

    const diffProps = PropsDiff.diff(nextScene, prevScene);
    let needsUpdate = false;

    // Reconcile fidelity state.
    frame.nextUseGarmentGeometry = (useGarmentGeometry ?? true) && isGarmentGeometryRequired(nextScene);

    // Reconcile garment geometry
    {
      const { garmentGeometry } = nextScene;

      // Move preloaded ownership to resources.
      if (garmentGeometry && diffProps.includes('garmentGeometry')) {
        // Use the same garment geometry ID as the one in entity reconciler.
        const garmentGeometryId = findWithGarment(nextScene)!._id;
        resources.garmentGeometries.move(garmentGeometryId, garmentGeometry);
      }

      if (garmentGeometry && frame.nextUseGarmentGeometry) {
        frame.nextGarmentGeometry = garmentGeometry;
      }
    }

    // Reconcile entities.
    if (diffProps.includes('children')) {
      {
        // Find root entity.
        const nextRoot = findRoot(nextScene);
        const prevRoot = prevScene ? findRoot(prevScene) : undefined;

        // Dispose root entity.
        if (!nextRoot && prevRoot) {
          this.deepDisposeEntity(prevRoot._id, prevScene, resources);
          needsUpdate = true;
        }

        // Reconcile root entity.
        if (nextRoot) {
          // 1st pass
          // - create and dispose renderables
          // - update independent renderables
          const root = this.getEntity(nextRoot._id) ?? createEntity(scene, nextRoot._id);
          needsUpdate = this.updateEntity1st(root, nextRoot, prevRoot) || needsUpdate;

          // 2nd pass
          // - seams depend on pieces
          // - paired piece buttons depend on pieces
          // - paired piece buttons lines depend on pieces
          needsUpdate = this.updateEntity2nd(root, nextRoot, prevRoot) || needsUpdate;
        }
      }

      {
        // Find environment entity.
        const nextEntity = findWithEnvironment(nextScene);
        const prevEntity = prevScene ? findWithEnvironment(prevScene) : undefined;
        const nextEnvironment = nextEntity?.components.environment;
        const prevEnvironment = prevEntity?.components.environment;

        // Reconcile environment.
        if (nextEnvironment && nextEnvironment !== prevEnvironment) {
          needsUpdate = this.updateEnvironment(scene, nextEnvironment, prevEnvironment) || needsUpdate;
        }

        // Dispose environment.
        if (!nextEnvironment && scene.environmentRef) {
          detachEnvironment(scene, resources);
          needsUpdate = true;
        }
      }

      // Update renderables still used in tools.
      renderables.clear();
      for (const [id, entity] of this.scene.entities) {
        if (entity.piece) {
          renderables.set(id, entity.piece.mesh);
        }
        if (entity.seam) {
          renderables.set(id, entity.seam.mesh);
        }
      }
    }

    this.state = nextScene;

    if (needsUpdate) {
      this.needsUpdate = true;
    }

    return needsUpdate;
  }

  // Update visibility
  updateVisible(visible: VisibilityState, outlined: Object3D[], hideSeamLines = false) {
    const { avatar, background, garmentHem, garmentPiece, garmentSeam, ground, logos } = visible;

    for (const [key, entity] of this.scene.entities) {
      const node = entity.node;
      const components = node.userData;

      if (components.avatar) {
        node.visible = avatar;
      } else if (components.garmentPiece) {
        node.visible = garmentPiece;

        if (components.garmentPiece?.logos?.length) {
          updateLogosVisibility(entity.piece!, logos);
        }
      } else if (components.garmentSeam) {
        node.visible = components.garmentSeam.sewingType === 'HEM' ? garmentHem : garmentSeam;
        entity.seam.mesh.visible =
          !components.TrueSeams &&
          !hideSeamLines &&
          (!components.garmentSeam.isClosed || this.isPieceOutlined(outlined, node));
      } else if (components.meshRenderable?.material === GROUND_MATERIAL) {
        node.visible = background;
        node.children[0].visible = ground;
      }
    }
  }

  /**
   * Update entity 1st pass.
   * @param renderable - Entity renderable.
   * @param nextProps Next entity state.
   * @param prevProps Prev entity state.
   */
  private updateEntity1st(
    renderable: EntityRenderable,
    nextProps: SceneEntity,
    prevProps: SceneEntity | undefined,
  ): boolean {
    const { frame, resources, scene } = this;
    let needsUpdate = false;

    // Update current
    needsUpdate = updateEntity1st(renderable, nextProps, prevProps, frame) || needsUpdate;

    // Update immediate children on the hierarchy.
    const nextScene = frame.nextScene;
    const nextChildren = nextProps ? filterEntitiesWithParent(nextScene.children, nextProps._id) : [];
    const prevScene = frame.prevScene;
    const prevChildren = prevProps ? filterEntitiesWithParent(prevScene.children, prevProps._id) : [];

    const [nextDiff, prevDiff] = ArrayDiff.diff(nextChildren, prevChildren);
    const entity = frame.entities.get(nextProps._id);
    entity.childrenDiff = nextDiff;

    for (const pair of nextDiff) {
      const nextChild = pair.next;
      const prevChild = pair.prev;
      const renderableChild = this.getEntity(nextChild._id) ?? createEntity(scene, nextChild._id, renderable);
      needsUpdate = this.updateEntity1st(renderableChild, nextChild, prevChild) || needsUpdate;
    }

    // Dispose entities no longer with us.
    for (const prev of prevDiff) {
      this.deepDisposeEntity(prev._id, prevScene, resources);
      needsUpdate = true;
    }

    return needsUpdate;
  }

  /**
   * Update entity 2nd pass.
   * @param renderable - Entity renderable.
   * @param nextProps Next entity state.
   * @param prevProps Prev entity state.
   */
  private updateEntity2nd(
    renderable: EntityRenderable,
    nextProps: SceneEntity,
    prevProps: SceneEntity | undefined,
  ): boolean {
    const { frame } = this;
    let needsUpdate = false;

    // Update current
    needsUpdate = updateEntity2nd(renderable, nextProps, prevProps, frame) || needsUpdate;

    const entity = frame.entities.get(nextProps._id);
    const childrenDiff = entity.childrenDiff;

    for (const pair of childrenDiff) {
      const nextChild = pair.next;
      const prevChild = pair.prev;
      const renderableChild = this.getEntity(nextChild._id);
      needsUpdate = this.updateEntity2nd(renderableChild, nextChild, prevChild) || needsUpdate;
    }

    return needsUpdate;
  }

  /**
   * Reconcile environment.
   * @param scene - Scene renderable.
   * @param nextProps - Next state.
   * @param prevProps - Prev state.
   */
  private updateEnvironment(
    scene: SceneRenderable,
    nextProps: EnvironmentComponent | undefined,
    prevProps: EnvironmentComponent | undefined,
  ): boolean {
    const { resources } = this;

    const diffProps = PropsDiff.diff(nextProps, prevProps);
    let dirty = false;

    // Reconcile environment.
    if (diffProps.includes('environment')) {
      const desc = nextProps.environment;
      detachEnvironment(scene, resources);

      if (resources.findEnvironment(desc.id)) {
        // Attach environment.
        attachEnvironment(scene, desc.id, resources);
        dirty = true;
      } else {
        // Register environment request.
        if (!resources.hasEnvironmentRequest(desc.id)) {
          resources.loadEnvironmentDeferred(desc);
        }
      }
    }

    // TODO: Reconcile environment intensity
    if (diffProps.includes('power')) {
      // scene.environmentIntensity = nextProps.power;
      // dirty = true;
    }

    return dirty;
  }

  /**
   *
   * @param outlined
   * @param child
   */
  private isPieceOutlined(outlined: Object3D[], child: Object3D): boolean {
    return !!outlined.find((outlinedChild) => {
      for (const lineChild of child.children) {
        if (outlinedChild.uuid.includes(lineChild.uuid)) {
          return true;
        }
      }
      return false;
    });
  }

  /**
   * Dispose en entity and all it's children
   * @param entityId - Entity Id.
   * @param sceneDesc Scene descriptor
   * @param resources Scene resources
   */
  private deepDisposeEntity(entityId: string, sceneDesc: SceneDesc, resources: ResourcesContext): void {
    const entityChildren = filterEntitiesWithParent(sceneDesc.children, entityId);

    // Dispose children
    for (const child of entityChildren) {
      this.deepDisposeEntity(child._id, sceneDesc, resources);
    }

    // Dispose entity
    disposeEntity(this.scene, entityId, resources);
  }
}
